/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  class GelishColorBrowser extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.range = this.querySelector('[data-color-range]');
      this.label = this.querySelector('[data-color-label]');
      this.groups = [...this.querySelectorAll('[data-color-group]')];

      this.range?.addEventListener('input', () => this.show(Number(this.range.value)), { signal });
      this.show(this.range ? Number(this.range.value) : 0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index) {
      if (!this.groups.length) return;
      const nextGroup = this.groups.find((group) => index <= Number(group.dataset.colorMax)) || this.groups.at(-1);
      this.groups.forEach((group) => {
        const active = group === nextGroup;
        group.hidden = !active;
        group.inert = !active;
      });

      const label = nextGroup.dataset.colorLabel || '';
      if (this.label) this.label.textContent = label;
      if (nextGroup.dataset.colorValue) this.style.setProperty('--gelish-color', nextGroup.dataset.colorValue);
      this.range?.setAttribute('aria-valuetext', label);
      nextGroup.querySelector('[data-slider-viewport]')?.scrollTo({ left: 0, behavior: 'auto' });
    }
  }

  class GelishStepTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.buttons = [...this.querySelectorAll('[data-gelish-step-index]')];
      this.groups = [...this.querySelectorAll('[data-gelish-step-group]')];

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gelish-step-index]');
        if (button) this.show(Number(button.dataset.gelishStepIndex));
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-gelish-step-index]');
        if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = this.buttons.indexOf(button);
        let next = current;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.buttons.length - 1;
        else if (event.key === 'ArrowRight') next = (current + 1) % this.buttons.length;
        else next = (current - 1 + this.buttons.length) % this.buttons.length;
        this.show(next, true);
      }, { signal });

      this.show(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index, focus = false) {
      if (!this.groups.length) return;
      const nextIndex = Math.min(this.groups.length - 1, Math.max(0, index));
      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === nextIndex;
        group.hidden = !active;
        group.inert = !active;
        group.classList.toggle('is-active', active);
        if (active) group.querySelector('product-instructions')?.show?.(0);
      });
      this.buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === nextIndex;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });
    }
  }

  if (!customElements.get('gelish-color-browser')) {
    customElements.define('gelish-color-browser', GelishColorBrowser);
  }
  if (!customElements.get('gelish-step-tabs')) {
    customElements.define('gelish-step-tabs', GelishStepTabs);
  }
})();
